import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useData } from '../data/DataProvider';
import { usePortalClient } from './usePortalClient';
import { money, date, invoiceTotal } from '../shared/formatters';
import Breadcrumbs from '../shared/components/Breadcrumbs';
import EmptyState from '../shared/components/EmptyState';
import ConfirmDialog from '../shared/components/ConfirmDialog';
import { useToast } from '../shared/components/ToastProvider';

// Portal quote detail (pass 5 §5G). A "sent" quote carries the Approve /
// Decline decision; approving requires typing your name (the pb_hook then
// forces approved_by_name from the client record server-side, stamps
// approved_at, and fires the agency notification).
export default function QuoteDetail() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { data, update } = useData();
  const { base, client } = usePortalClient();
  const { notify } = useToast();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState(null); // 'decline'

  const quote = data.quotes.find((item) => item.id === quoteId);
  if (!quote || quote.client_id !== client.id) {
    return (
      <div className="portal-card">
        <EmptyState title="Quote not found" action="Back to quotes" onAction={() => navigate(`${base}/quotes`)} />
      </div>
    );
  }

  const approve = async () => {
    if (!name.trim()) {
      notify('Type your name to confirm the approval', 'error');
      return;
    }
    setBusy('approve');
    try {
      await update('quotes', quote.id, { ...quote, status: 'approved', approved_by_name: name.trim() });
      notify('Quote approved — thank you!');
    } catch (err) {
      notify(err.message || 'Could not approve the quote', 'error');
    } finally {
      setBusy('');
    }
  };

  const decline = async () => {
    setBusy('decline');
    try {
      await update('quotes', quote.id, { ...quote, status: 'declined' });
      notify('Quote declined — we will be in touch.');
      setConfirm(null);
    } catch (err) {
      notify(err.message || 'Could not decline the quote', 'error');
    } finally {
      setBusy('');
    }
  };

  const pill = quote.status === 'sent' ? 'awaiting approval' : quote.status;

  return (
    <div className="portal-card invoice-detail">
      <Breadcrumbs items={[{ label: 'Quotes', to: `${base}/quotes` }, { label: quote.number }]} />

      <div className="panel-head">
        <div>
          <span className="eyebrow">Quote</span>
          <h1>{quote.number}</h1>
          <p className="field-hint">
            Issued {date(quote.issued_at)}
            {quote.valid_until ? ` · valid until ${date(quote.valid_until)}` : ''}
          </p>
        </div>
        <span className={`status-pill status-${quote.status === 'approved' ? 'paid' : quote.status === 'declined' ? 'overdue' : quote.status === 'expired' ? 'cancelled' : quote.status}`}>
          <span className="status-dot" />{pill}
        </span>
      </div>

      <p>{quote.title}</p>

      <table>
        <thead>
          <tr><th>Description</th><th>Qty</th><th>Price</th><th>Total</th></tr>
        </thead>
        <tbody>
          {quote.line_items.map((item) => (
            <tr key={item.id}>
              <td>{item.description}</td>
              <td>{item.quantity}</td>
              <td>{money(item.unit_price)}</td>
              <td>{money(item.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="invoice-total"><span>Total</span><strong>{money(invoiceTotal(quote))}</strong></div>
      {quote.notes ? <p className="field-hint">{quote.notes}</p> : null}

      {quote.status === 'sent' && (
        <div className="quote-decision">
          <p className="field-hint">Do you want to go ahead with this work? Your approval is a confirmation, not a contract.</p>
          <label>Type your name to confirm approval
            <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Full name" />
          </label>
          <div className="invoice-actions">
            <button className="primary-button" onClick={approve} disabled={busy === 'approve' || busy === 'decline'}>
              {busy === 'approve' ? 'Approving…' : 'Approve this quote'}
            </button>
            <button className="secondary-button" onClick={() => setConfirm('decline')} disabled={busy === 'approve' || busy === 'decline'}>
              Decline
            </button>
          </div>
        </div>
      )}

      {quote.status === 'approved' && quote.approved_by_name && (
        <p className="field-hint">
          Approved by {quote.approved_by_name}{quote.approved_at ? ` on ${date(quote.approved_at)}` : ''} — thank you.
        </p>
      )}

      <Link className="back-link" to={`${base}/quotes`}>Back to quotes</Link>

      <ConfirmDialog
        open={Boolean(confirm)}
        title="Decline this quote?"
        message="We will be notified and can discuss alternatives. You can change your mind until the quote expires."
        confirmLabel="Decline quote"
        onClose={() => setConfirm(null)}
        onConfirm={decline}
      />
    </div>
  );
}
